/**
 * Email Verification Handler
 *
 * Handles Firebase email verification flow:
 * - Applies verification code from email link
 * - Shows success/error messages
 * - Provides resend option
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { useFirebaseAuth } from '@/features/auth/hooks/useFirebaseAuth';

/**
 * EmailVerificationHandler Component
 *
 * This component handles email verification when users click the link in their email.
 * Firebase uses mode=verifyEmail and oobCode (out-of-band code) parameters.
 */
export const EmailVerificationHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useFirebaseAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    // If not in verification mode, do nothing
    if (mode !== 'verifyEmail' || !oobCode) {
      return;
    }

    handleEmailVerification();
  }, [mode, oobCode]);

  const handleEmailVerification = async () => {
    try {
      setStatus('loading');
      setMessage('');

      const { applyActionCode } = await import('firebase/auth');
      const auth = firebaseAuthService.getAuth();

      if (!auth) {
        throw new Error('Authentication not initialized');
      }

      // Apply the email verification action
      await applyActionCode(auth, oobCode!);

      setStatus('success');
      setMessage('Your email has been successfully verified!');

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/client/dashboard');
      }, 3000);
    } catch (error: unknown) {
      const authError = error as { code?: string; message?: string };

      setStatus('error');

      if (authError.code === 'auth/invalid-action-code') {
        setMessage('The verification link is invalid or has expired. Please request a new verification email.');
      } else {
        setMessage(authError.message || 'Failed to verify email. Please try again.');
      }

      console.error('Email verification error:', error);
    }
  };

  const handleResendVerification = async () => {
    if (!user) {
      setMessage('Please sign in to resend verification email.');
      return;
    }

    try {
      setIsResending(true);
      setMessage('');

      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(user);

      setMessage('Verification email sent! Please check your inbox.');
    } catch (error: unknown) {
      const err = error as { message?: string };
      setMessage(err.message || 'Failed to send verification email. Please try again.');
      console.error('Resend verification error:', error);
    } finally {
      setIsResending(false);
    }
  };

  // If not in verification mode, don't render anything
  if (mode !== 'verifyEmail') {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 text-center">
          {/* Header */}
          <div className="mb-6">
            {status === 'loading' && (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-error-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-error-600" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              {status === 'loading' && 'Verifying Your Email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </h1>
            <p className="text-neutral-600">
              {status === 'loading' && 'Please wait while we verify your email address.'}
              {status === 'success' && message}
              {status === 'error' && message}
            </p>
          </div>

          {/* Actions */}
          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Resend Verification Email
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="w-full px-4 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-xl font-medium transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          )}

          {status === 'success' && (
            <button
              onClick={() => navigate('/client/dashboard')}
              className="w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
            >
              Continue to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * UnverifiedUserBanner Component
 *
 * Shows a warning banner when the user's email is not verified.
 * Provides option to resend verification email.
 */
interface UnverifiedUserBannerProps {
  emailVerified?: boolean;
  onResendVerification?: () => Promise<void>;
}

export const UnverifiedUserBanner: React.FC<UnverifiedUserBannerProps> = ({
  emailVerified = false,
  onResendVerification,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [message, setMessage] = useState('');

  // Don't show banner if email is verified
  if (emailVerified || !showBanner) {
    return null;
  }

  const handleResend = async () => {
    if (onResendVerification) {
      try {
        setIsResending(true);
        setMessage('');
        await onResendVerification();
        setMessage('Verification email sent! Please check your inbox.');
      } catch (error) {
        setMessage('Failed to send verification email. Please try again.');
      } finally {
        setIsResending(false);
      }
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-1">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">Email not verified.</span>{' '}
              Please check your inbox and spam folder to verify your email address.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {message && (
              <span className="text-sm text-amber-700">{message}</span>
            )}
            <button
              onClick={handleResend}
              disabled={isResending}
              className="text-sm font-medium text-amber-800 hover:text-amber-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : 'Resend email'}
            </button>
            <button
              onClick={() => setShowBanner(false)}
              className="text-amber-600 hover:text-amber-800"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationHandler;
